import type { CrudFilters } from '@refinedev/core';
import { ProductFormat } from '@/api/generated/schemas';
import type { SummaryBarQuery } from '@/components/common/SummaryAlertsBar';

export type MasterFileViewKey =
  | 'all'
  | 'not_downloaded'
  | 'transcoding_incomplete'
  | 'transcoding_missing'
  | 'available_now'
  | 'downloaded';

export type MasterFileView = SummaryBarQuery<MasterFileViewKey>;

/**
 * Formats these alerts ignore. Written as an exclusion, so a format added to
 * the catalog later is watched by default rather than silently skipped.
 */
export const IGNORED_FILE_FORMATS = [ProductFormat.mobi, ProductFormat.pdf];

/** Availability that is either running now or still ahead of us. */
export const CURRENT_OR_FUTURE_AVAILABILITY = ['available', 'upcoming'];

/** Superseded files are history: every alert is about the current one. */
const LATEST: CrudFilters = [
  { field: 'is_latest', operator: 'eq', value: true },
];

/** Files behind a product a reader can open now or soon. */
const LIVE: CrudFilters = [
  {
    field: 'availability',
    operator: 'in',
    value: CURRENT_OR_FUTURE_AVAILABILITY,
  },
];

const UNDER_CONTRACT: CrudFilters = [
  { field: 'publisher_has_active_contract', operator: 'eq', value: true },
];

const WATCHED_FORMAT: CrudFilters = [
  { field: 'file_format', operator: 'nin', value: IGNORED_FILE_FORMATS },
];

/** The files this bar is about, before each alert's own question. */
const WATCHED: CrudFilters = [
  ...LATEST,
  ...LIVE,
  ...UNDER_CONTRACT,
  ...WATCHED_FORMAT,
];

/** Every transcoding outcome except the one that means "done". */
export const UNFINISHED_TRANSCODING_STATUSES = [
  'pending',
  'requested',
  'failed',
  'superseded',
];

/**
 * Filter sets behind the "Wymaga uwagi" bar, derived from the list endpoint
 * (there is no dedicated stats endpoint). Order is display order.
 */
export const summaryQueries = (): MasterFileView[] => [
  {
    // Files we are expected to hold but do not — they never came down.
    key: 'not_downloaded',
    severity: 'error',
    filters: [
      ...WATCHED,
      { field: 'is_downloaded', operator: 'eq', value: false },
    ],
  },
  {
    // Here but not usable yet: transcoding ran and did not finish — queued,
    // in flight, failed or superseded.
    key: 'transcoding_incomplete',
    severity: 'warning',
    filters: [
      ...WATCHED,
      { field: 'is_downloaded', operator: 'eq', value: true },
      {
        field: 'transcoding_status',
        operator: 'in',
        value: UNFINISHED_TRANSCODING_STATUSES,
      },
    ],
  },
  {
    // A downloaded file with no job at all. `in` cannot reach it — SQL's NULL
    // matches no list — so the other half of "not completed" is its own pill.
    key: 'transcoding_missing',
    severity: 'warning',
    filters: [
      ...WATCHED,
      { field: 'is_downloaded', operator: 'eq', value: true },
      { field: 'transcoding_status', operator: 'null', value: true },
    ],
  },
  {
    key: 'available_now',
    filters: [
      ...LATEST,
      { field: 'availability', operator: 'eq', value: 'available' },
    ],
  },
  {
    // The figure the alerts read against: watched files we do hold.
    key: 'downloaded',
    filters: [
      ...WATCHED,
      { field: 'is_downloaded', operator: 'eq', value: true },
    ],
  },
];

export const alertQueries = (): MasterFileView[] =>
  summaryQueries().filter((q) => q.severity != null);

export const statQueries = (): MasterFileView[] =>
  summaryQueries().filter((q) => q.severity == null);

/** Options of the "Widok" select: everything, then the alerts. */
export const viewQueries = (): MasterFileView[] => [
  { key: 'all', filters: [] },
  ...alertQueries(),
];

export const masterFileView = (
  key: MasterFileViewKey | null
): MasterFileView | null =>
  key ? (viewQueries().find((v) => v.key === key) ?? null) : null;

/** Whether `field` is one of the filters `key` itself sets. */
export const viewOwnsField = (
  key: MasterFileViewKey | null,
  field: string
): boolean =>
  (masterFileView(key)?.filters ?? []).some(
    (f) => 'field' in f && f.field === field
  );
